import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { integracoesApi, type Integracao, type StatusIntegracao } from '@/lib/api/core';
import { useAuthStore } from '@/lib/stores/authStore';
import { toast } from '@/lib/stores/toastStore';
import { formatarData } from '@/lib/format';
import { Info } from '@/design-system/Info';

const CHIP: Record<StatusIntegracao, string> = {
  conectada: 'good',
  desconectada: 'neutral',
  erro: 'crit',
  pendente: 'warn',
};

const ROTULO: Record<StatusIntegracao, string> = {
  conectada: 'Conectada',
  desconectada: 'Desconectada',
  erro: 'Com erro',
  pendente: 'Pendente',
};

/**
 * As integrações que os comandos de carga escrevem de volta. Não é vocabulário
 * novo: são exatamente as chaves procuradas por
 * `apps/api/src/infra/database/tse-importar.ts` (`chave: 'tse_carga'`) e
 * `ibge-importar.ts` (`chave: 'dados_publicos'`). Qualquer outra integração que
 * venha a gravar `parametros.ultima_carga` entra na tabela sozinha.
 */
const CHAVES_CARGA = new Set(['tse_carga', 'dados_publicos']);

/** Campos de `ultima_carga` que descrevem o recorte, não o volume. */
const NAO_VOLUME = new Set(['uf', 'ciclo', 'segundos']);

const ehCarga = (i: Integracao) =>
  CHAVES_CARGA.has(i.chave) || !!(i.parametros as any)?.ultima_carga;

/** `{uf:'CE',ciclo:2024}` → `CE · 2024`; sem uf nem ciclo, `—`. */
function escopoDaCarga(carga: Record<string, any> | undefined): string {
  if (!carga) return '—';
  return [carga.uf, carga.ciclo].filter((v) => v !== undefined && v !== null && v !== '').join(' · ') || '—';
}

/**
 * Os volumes são os que o próprio comando gravou — a carga do TSE conta
 * candidatos/candidaturas/resultados/detalhes, a do IBGE conta municípios.
 * Ler as chaves do objeto em vez de cravar colunas evita apresentar o volume
 * de uma carga com o rótulo da outra, e não soma o que não é somável
 * (`comPopulacao` é subconjunto de `municipios`, não parcela dele).
 */
function volumesDaCarga(carga: Record<string, any> | undefined): string {
  if (!carga) return '';
  const partes = Object.entries(carga)
    .filter(([k, v]) => !NAO_VOLUME.has(k) && typeof v === 'number')
    .map(([k, v]) => `${k}: ${v.toLocaleString('pt-BR')}`);
  return partes.join(' · ');
}

export function IntegPage() {
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();

  const erroDe = (err: any, padrao: string) => {
    const msg = err?.response?.data?.message;
    return Array.isArray(msg) ? msg.join(' · ') : msg || padrao;
  };

  const { data: integracoes = [], isLoading } = useQuery({
    queryKey: ['integracoes'],
    queryFn: () => integracoesApi.list(token),
    enabled: !!token,
  });

  const atualizar = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Partial<Integracao> }) =>
      integracoesApi.update(id, dados, token),
    onSuccess: (i) => {
      queryClient.invalidateQueries({ queryKey: ['integracoes'] });
      toast(`${i.nome}: ${ROTULO[i.status].toLowerCase()}.`);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao atualizar a integração.')),
  });

  const conectadas = integracoes.filter((i) => i.status === 'conectada').length;
  const comErro = integracoes.filter((i) => i.status === 'erro').length;
  const cargas = integracoes.filter(ehCarga);

  return (
    <div className="page" id="p-integracoes">
      <div className="head">
        <div>
          <h1 style={{ color: 'var(--eleg)' }}>Ingestão TSE e dados públicos</h1>
          <p>
            {/*
              O que estava aqui era uma promessa sobre um artefato inexistente:
              "qualquer mapeamento entre seções eleitorais e territórios tem
              versão e responsável técnico declarado". Não existe tabela de
              correspondência seção↔bairro nem setor↔bairro em nenhuma
              migration — a ausência é o bloqueio conhecido, já documentado em
              `MatrizPage.tsx`, `MatrizForcaMomento.tsx` e
              `compromissos-lideranca.service.ts`.
            */}
            Pipeline de carga dos dados abertos para os schemas eleg e dadospub. TSE e IBGE têm
            importador real, rodado por linha de comando; CNES, INEP e SNIS aparecem como rótulo e
            ainda não têm carga.
          </p>
        </div>
      </div>

      {/*
        NOTA DE MÉTODO, proto:768 — invertida.

        O protótipo chama de "risco principal" o fato de as tabelas de
        correspondência serem "construídas pela equipe, versionadas e
        assinadas", porque um erro ali produziria número com aparência de
        precisão. Aqui o risco é anterior: as tabelas não existem, então o
        recorte por território simplesmente não sai da apuração do TSE. Manter
        a frase do protótipo descreveria um controle que ninguém exerce.
      */}
      <div className="note warn" data-testid="nota-risco-principal" style={{ marginBottom: 12 }}>
        <b>Risco principal</b> A correspondência entre seção eleitoral e bairro não existe: nem o
        TSE nem o IBGE a publicam, e ela também não está neste banco. Por isso a apuração fica por
        zona eleitoral e nenhuma tela atribui voto a um território.{' '}
        <Info titulo="Por que a correspondência entre seção e território não existe">
          A carga do TSE grava <span className="mono">secao: null</span> e o cadastro de território
          não guarda zona. Enquanto alguém não construir essa tabela, versioná-la e assumir a
          autoria dela, o recorte por território não sai da apuração.
        </Info>
      </div>

      {/* KPIs */}
      <div className="grid g3">
        <div className="card kpi">
          <div className="l">Conectadas</div>
          <div className="v" style={{ color: 'var(--good)' }}>{conectadas}</div>
          <div className="d">de {integracoes.length} previstas</div>
        </div>
        <div className="card kpi">
          <div className="l">Com erro</div>
          <div className="v" style={{ color: comErro ? 'var(--crit)' : 'var(--ink)' }}>
            {comErro}
          </div>
          <div className="d">exigem reconfiguração</div>
        </div>
        <div className="card kpi">
          <div className="l">
            Credenciais{' '}
            <Info titulo="Onde ficam os tokens das integrações">
              Tokens e segredos não são guardados neste cadastro. Aqui ficam apenas o estado da
              conexão e os parâmetros não sensíveis.
            </Info>
          </div>
          <div className="v" style={{ fontSize: 18 }}>
            Fora daqui
          </div>
          <div className="d">esta tela guarda só estado e parâmetros</div>
        </div>
      </div>

      {/* Cargas — o que os comandos de importação gravaram de volta */}
      <div className="card flat tw" style={{ marginTop: 12 }}>
        <h2 style={{ padding: '0 0 8px' }}>Cargas de dados públicos</h2>
        <table aria-label="Cargas de dados públicos">
          <thead>
            <tr>
              <th>Fonte</th>
              <th>Escopo</th>
              <th>Volumes gravados</th>
              <th>Situação</th>
              <th>Última execução</th>
            </tr>
          </thead>
          <tbody>
            {cargas.map((i) => {
              const carga = (i.parametros as any)?.ultima_carga as Record<string, any> | undefined;
              const volumes = volumesDaCarga(carga);
              return (
                <tr key={i.id}>
                  <td>{i.nome}</td>
                  <td>{escopoDaCarga(carga)}</td>
                  <td style={{ fontSize: 12 }}>{volumes || 'nunca executada'}</td>
                  <td>
                    <span className={`chip ${CHIP[i.status]}`}>{ROTULO[i.status]}</span>
                  </td>
                  <td>
                    {i.ultima_sincronizacao ? formatarData(i.ultima_sincronizacao) : 'nunca executada'}
                  </td>
                  {i.ultimo_erro ? (
                    <td style={{ color: 'var(--crit)', fontSize: 12 }}>{i.ultimo_erro}</td>
                  ) : null}
                </tr>
              );
            })}
            {cargas.length === 0 && (
              <tr>
                <td colSpan={5} className="empty">
                  Nenhuma integração de carga registrada.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="note warn" style={{ marginTop: 10, fontSize: 12 }}>
        <b>A carga não é executada por esta tela.</b> A importação é comando de linha, rodado onde
        o arquivo está:{' '}
        <Info titulo="Por que a importação é comando de linha">
          Nenhuma rota do servidor roda a importação: os arquivos do TSE passam de 100 MB e o
          processamento leva minutos, o que não cabe em requisição HTTP. Um botão que apenas
          exibisse "carga concluída" mentiria.
        </Info>
        <br />
        <code className="mono">
          pnpm --filter @eleg360/api tse:importar -- --ciclo 2024 --uf CE
        </code>
        <br />
        <code className="mono">pnpm --filter @eleg360/api ibge:importar -- --uf CE</code>
        <br />O comando grava o resultado de volta nesta tela: é dele que vêm a situação, os
        volumes e a data acima.
      </div>

      {/* Lista */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {integracoes.map((i) => (
          <div key={i.id} className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h2 style={{ margin: 0, fontSize: 15 }}>{i.nome}</h2>
                  <span className={`chip ${CHIP[i.status]}`}>{ROTULO[i.status]}</span>
                </div>
                <p style={{ fontSize: 13, color: 'var(--ink2)', margin: '6px 0 0' }}>{i.descricao}</p>

                <dl className="dl" style={{ marginTop: 8, fontSize: 12 }}>
                  <dt>Chave técnica</dt>
                  <dd className="mono">{i.chave}</dd>
                  <dt>Última sincronização</dt>
                  <dd>{i.ultima_sincronizacao ? formatarData(i.ultima_sincronizacao) : 'nunca'}</dd>
                  {i.ultimo_erro && (
                    <>
                      <dt>Último erro</dt>
                      <dd style={{ color: 'var(--crit)' }}>{i.ultimo_erro}</dd>
                    </>
                  )}
                </dl>
              </div>

              <div className="field" style={{ minWidth: 190 }}>
                <label style={{ fontSize: 11 }}>Situação</label>
                <select
                  className="in"
                  value={i.status}
                  disabled={atualizar.isPending}
                  onChange={(e) =>
                    atualizar.mutate({
                      id: i.id,
                      dados: { status: e.target.value as StatusIntegracao },
                    })
                  }
                >
                  <option value="desconectada">Desconectada</option>
                  <option value="pendente">Pendente de configuração</option>
                  <option value="conectada">Conectada</option>
                  <option value="erro">Com erro</option>
                </select>
              </div>
            </div>
          </div>
        ))}

        {isLoading && <div className="empty">Carregando integrações...</div>}
        {!isLoading && integracoes.length === 0 && (
          <div className="empty">Nenhuma integração registrada para este mandato.</div>
        )}
      </div>

    </div>
  );
}
